import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UserPromotionActions } from '@app/client/account/team/promotion/actions';
import * as fromUserPromotions from '@app/client/account/team/promotion/reducers/promotion.reducer';
import * as fromRoot from '@app/reducers';
import { SmashPromotionComponent } from '@app/shared/modules/smash-promotion/smash-promotion/smash-promotion.component';
import { Promotion } from '@app/shared/models/promotion';
import { DialogService } from '@ngneat/dialog';
import { select, Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { first, take } from 'rxjs/operators';
import { Constant } from 'src/constant';
import { ModalPromotionUpdateAddBackgroundComponent } from './modal-promotion-update-add-background/modal-promotion-update-add-background.component';

@Component({
  selector: 'app-promotion-update',
  templateUrl: './promotion-update.component.html',
  styleUrls: ['./promotion-update.component.scss'],
  animations: [
    trigger('headerAnimation', [
      state('in', style({ transform: 'translateX({{startPosition}})' }), { params: { startPosition: '0px' } }),
      state('out', style({ transform: 'translateX({{endPosition}})' }), { params: { endPosition: '-350px' } }),
      transition('in => out', animate('0.4s ease-in-out')),
      transition('out => in', animate('0.4s linear'))
    ]),
  ],
})
export class PromotionUpdateComponent implements OnInit {

  @ViewChild(SmashPromotionComponent, { static: true }) promotionComponent: SmashPromotionComponent;

  promotionToUpdate$: Observable<Promotion>;
  promotionUpdating$: Observable<boolean>;
  modal$: Observable<any>;
  supportedLanguages$: Observable<any[]>;
  selectedLanguage$: Observable<any>;

  subscriptions: Subscription[] = [];
  promotionId: string;
  selectedMedia: any;
  creatingMedia = false;
  modalDisplayed = false;
  selectionVideo = false;
  promotionEdited = false;
  backgroundIsLoading = false;
  error = false;
  sourceUrl = null;
  promotionUpdateForm: FormGroup;
  checkboxStatus: boolean;
  submitted = false;
  timer: any;
  action: any;
  medias: any[];

  actions: any[] = Constant.callToActionsList;
  targets = [
    { value: 'External', label: 'PREMIUM_PROMOTION_OPTION_EXTERNAL' },
    { value: 'Internal', label: 'PREMIUM_PROMOTION_OPTION_INTERNAL' }
  ];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private store$: Store<fromRoot.State & fromUserPromotions.State>,
    private readonly fb: FormBuilder,
    private dialog: DialogService,
  ) {
    this.route.params.pipe(take(1)).subscribe(({ id: teamId, promotionId }) => {
      this.store$.dispatch(UserPromotionActions.loadPromotion({ teamId, promotionId }));
    });

    this.buildForm();

    this.promotionToUpdate$ = this.store$.pipe(select(fromUserPromotions.getPromotionToUpdate));
    this.promotionUpdating$ = this.store$.pipe(select(fromUserPromotions.getPromotionUpdating));

    this.supportedLanguages$ = this.store$.pipe(select(fromRoot.getSupportedLanguages));
    this.selectedLanguage$ = this.store$.pipe(select(fromRoot.getSelectedLanguage));
  }

  buildForm() {
    this.promotionUpdateForm = this.fb.group({
      title: ['', Validators.required],
      teaser: ['', Validators.required],
      text: [''],
      language: ['', Validators.required],
      button: ['', Validators.required],
      link: ['', [Validators.required, Validators.pattern(Constant.regexpPromotionLink)]],
      target: ['', Validators.required],
      status: ['', Validators.required],
    });
  }

  async setupFormData() {
    const promotionToUpdate = await this.promotionToUpdate$.pipe(first(promotion => !!promotion)).toPromise();
    this.action = this.actions.find(action => promotionToUpdate.button === action.value);
    this.promotionUpdateForm.patchValue({
      title: promotionToUpdate.title,
      teaser: promotionToUpdate.teaser ? promotionToUpdate.teaser : '',
      text: promotionToUpdate.text ? promotionToUpdate.text : '',
      button: this.action,
      link: promotionToUpdate.link,
      language: promotionToUpdate.language,
      target: promotionToUpdate.target,
      status: promotionToUpdate.status
    });
    this.checkboxStatus = promotionToUpdate?.status === 'Enabled';
    this.onReplay();
  }

  back() {
    this.route.params.pipe(take(1)).subscribe(params => {
      this.router.navigateByUrl('/account/team/' + params.id + '/promotions');
    });
  }

  listenChangesFromPromotionToUpdate() {
    this.promotionToUpdate$.subscribe((promotion: any) => {
      if (promotion) {
        this.medias = promotion.medias;
        this.creatingMedia = false;
      }
    });
  }

  listenChangesForm() {
    this.promotionUpdateForm.valueChanges.subscribe(
      value => {
        this.checkboxStatus = value.status;
        value.status = value.status ? 'Enabled' : 'Disabled';

        this.store$.dispatch(UserPromotionActions.updatePromotionToUpdateTemp({ data: value }));
      }
    );
  }

  async ngOnInit(): Promise<void> {
    await this.setupFormData();
    this.listenChangesFromPromotionToUpdate();
    this.listenChangesForm();
  }

  get f() { return this.promotionUpdateForm.controls; }

  onReplay() {
    this.promotionComponent.replay();
  }

  onAddMedia() {
    this.dialog.open(ModalPromotionUpdateAddBackgroundComponent, {
      closeButton: true,
      enableClose: true,
      size: 'sm',
    });
  }

  onRemoveMedia(media) {
    this.store$.dispatch(UserPromotionActions.removeMediaFromPromotionToUpdate({ media }));
  }

  onDeleteMedia(media, event) {
    this.store$.dispatch(UserPromotionActions.removeMediaFromPromotionToUpdate({ media }));
    event.stopPropagation();
  }

  onSubmit() {
    this.submitted = true;
    if (this.promotionUpdateForm.valid) {
      this.store$.dispatch(UserPromotionActions.updatePromotion());
      this.submitted = false;
    } else {
      return false;
    }
  }

  resolveSrcUrlString(src: string): string {
    if (src?.startsWith('http')) {
      return `url(${src})`;
    } else {
      return `url(//${src})`;
    }
  }

  selectMedia(media) {
    if (media) {
      const index = media.id ? this.medias.findIndex(_media => media.id === _media.id) : this.medias.findIndex(_media => media.object_id === _media.object_id);
      this.selectedMedia = { ...media, index };
    } else {
      return null;
    }
  }

  isMediaSelected(media: any) {
    if (this.selectedMedia) {
      return media.id === this.selectedMedia.id;
    }
  }

  get indexSelected() {
    return this.selectedMedia ? this.selectedMedia.index : null;
  }

  get isUrlValid() {
    return this.sourceUrl ? (Constant.regexpYoutube.test(this.sourceUrl) || Constant.regexpVimeo.test(this.sourceUrl)) : false;
  }
}
